"use client";

import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/utils/supabase/client';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Users, Mail, UserPlus, Trash2 } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { showSuccess, showError } from '@/utils/toast';
import { InviteForm } from '@/components/InviteForm';
import { InviteList } from '@/components/InviteList'; // Import InviteList
import { Skeleton } from '@/components/ui/skeleton';

const UserManagement = () => {
  const { profile, company } = useAuth();
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [activeTab, setActiveTab] = useState<'users' | 'invites'>('users');

  // Fetch users for the company (with error handling)
  const { data: users, isLoading: usersLoading, refetch: refetchUsers } = useQuery({
    queryKey: ['admin-users', company?.id],
    queryFn: async () => {
      if (!company?.id) {
        console.warn('UserManagement: No company ID - skipping users query');
        return [];
      }
      const { data, error } = await supabase
        .from('profiles')
        .select('id, email, first_name, last_name, role, created_at')
        .eq('company_id', company.id)
        .order('created_at', { ascending: false });
      
      if (error) {
        if (error.message.includes('permission')) {
          showError('Access denied. Contact superadmin.');
          return []; // Fallback data
        }
        throw error; // Re-throw for retry
      }
      return data || [];
    },
    enabled: !!company?.id && profile?.role === 'admin', // Guard: Only for admins
    retry: (failureCount, error) => {
      if (error.message.includes('permission')) return false; // Don't retry permission errors
      return failureCount < 3;
    },
  });

  // Delete user mutation (admin only)
  const deleteMutation = useMutation({
    mutationFn: async (userId: string) => {
      const { error } = await supabase.from('profiles').delete().eq('id', userId);
      if (error) throw error;
    },
    onSuccess: () => {
      refetchUsers(); // Refresh users list
      showSuccess('User deleted!');
    },
    onError: (error) => {
      if (error.message.includes('permission')) {
        showError('Access denied. Cannot delete users.');
      } else {
        showError('Failed to delete user');
      }
    },
  });

  if (usersLoading) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <Card key={i}>
            <CardHeader>
              <Skeleton className="h-4 w-48" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-4 w-full" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">User Management</h1>
        <Button onClick={() => setShowInviteForm(!showInviteForm)}>
          <UserPlus className="mr-2 h-4 w-4" />
          {showInviteForm ? 'Cancel' : 'Invite User'}
        </Button>
      </div>

      {/* Tabs for Users and Invites */}
      <Tabs value={activeTab} onValueChange={(value: string) => setActiveTab(value as 'users' | 'invites')} className="w-full">
        <TabsList>
          <TabsTrigger value="users">Users ({users?.length || 0})</TabsTrigger>
          <TabsTrigger value="invites">Invites</TabsTrigger>
        </TabsList>

        <TabsContent value="users">
          {showInviteForm && (
            <Card className="mb-6">
              <CardHeader>
                <CardTitle>Invite New User</CardTitle>
              </CardHeader>
              <CardContent>
                <InviteForm companyId={company?.id || ''} />
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Company Users ({users?.length || 0})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {users?.map((user) => (
                  <div key={user.id} className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex-1">
                      <div className="font-medium">{user.first_name} {user.last_name}</div>
                      <div className="text-sm text-muted-foreground">{user.email}</div>
                      <Badge variant={user.role === 'admin' ? "default" : "secondary"} className="mt-1">
                        {user.role}
                      </Badge>
                    </div>
                    <div className="flex space-x-2">
                      <Button variant="outline" size="sm">Edit</Button>
                      <Button 
                        variant="destructive" 
                        size="sm" 
                        onClick={() => deleteMutation.mutate(user.id)}
                        disabled={deleteMutation.isPending}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
              {users?.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  No users yet. Invite your first team member!
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="invites">
          <InviteList companyId={company?.id || ''} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default UserManagement;